import type { RegistrantLicenseListLicenseResponseSchema } from "@/hera-api/client/types.gen";
import { attentionColumnParts } from "@/lib/attention-column";
import { jurisdictionOptionLabel } from "@/lib/jurisdiction-option-label";
import { licenseMatchesSelectedAms } from "@/lib/registrant-ams-utils";
import {
  matchesRegistrantBalanceDueTileCohort,
  matchesRegistrantRenewableTileCohort,
} from "@/lib/registrant-balance-due-cohort";
import type { JurisdictionLicenses } from "@/types/license";
import * as React from "react";

export interface RegistrantOption {
  value: string;
  label: string;
}

interface UseDashboardDataProps {
  data:
    | {
        licenses: RegistrantLicenseListLicenseResponseSchema[];
        has_multiple_registrant_accounts?: boolean;
      }
    | undefined;
  selectedJurisdictions: string[];
  selectedLicenseTypes: string[];
  /** When set, filter to licenses owned by this registrant account (company with >1 user). */
  selectedRegistrantAccountId: string | null;
  selectedAms?: string | null;
  amsQuestionId?: string | null;
}

function filterLicensesByRegistrant<
  T extends { registrant_account_id?: string | null },
>(licenses: T[], selectedRegistrantAccountId: string | null): T[] {
  if (!selectedRegistrantAccountId) return licenses;
  return licenses.filter(
    (l) =>
      l.registrant_account_id != null &&
      l.registrant_account_id === selectedRegistrantAccountId,
  );
}

export function useDashboardData({
  data,
  selectedJurisdictions,
  selectedLicenseTypes,
  selectedRegistrantAccountId,
  selectedAms = null,
  amsQuestionId = null,
}: UseDashboardDataProps) {
  const has_multiple_registrant_accounts =
    data?.has_multiple_registrant_accounts ?? false;

  // Registrant filter options (unique account id + display name) when company has multiple users
  const registrantOptions: RegistrantOption[] = React.useMemo(() => {
    if (!has_multiple_registrant_accounts || !data?.licenses) return [];
    const seen = new Set<string>();
    const options: RegistrantOption[] = [];
    for (const license of data.licenses) {
      const id = license.registrant_account_id;
      const name = license.registrant_display_name ?? id ?? "";
      if (id && !seen.has(id)) {
        seen.add(id);
        options.push({ value: id, label: name || id });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [has_multiple_registrant_accounts, data?.licenses]);

  // Transform the data into the desired structure (after registrant filter for license list)
  const jurisdictionLicenses: JurisdictionLicenses[] = React.useMemo(() => {
    if (!data?.licenses) return [];
    const licenses = filterLicensesByRegistrant(
      data.licenses,
      selectedRegistrantAccountId,
    ).filter((license) =>
      licenseMatchesSelectedAms(license, selectedAms, amsQuestionId ?? null),
    );
    return licenses.reduce((acc, license) => {
      const jurisdiction = license.jurisdiction;
      const existingJurisdiction = acc.find(
        (j) => j.display_code === jurisdiction.display_code,
      );

      if (existingJurisdiction) {
        existingJurisdiction.licenses.push(license);
      } else {
        acc.push({
          display_code: jurisdiction.display_code,
          name: jurisdiction.name,
          county: jurisdiction.county,
          state: jurisdiction.state,
          licenses: [license],
        });
      }

      return acc;
    }, [] as JurisdictionLicenses[]);
  }, [data?.licenses, selectedRegistrantAccountId, selectedAms, amsQuestionId]);

  /**
   * Position of each license in the API response. Server-sorted pages arrive in
   * the requested sort order, which jurisdiction grouping would otherwise
   * regroup before the bulk table renders it.
   */
  const serverRowOrder = React.useMemo(() => {
    const order = new Map<string, number>();
    (data?.licenses ?? []).forEach((license, index) => {
      order.set(license.id, index);
    });
    return order;
  }, [data?.licenses]);

  // Get jurisdiction options for the filter
  const jurisdictionOptions = React.useMemo(
    () =>
      jurisdictionLicenses.map((j) => ({
        value: j.display_code,
        label: jurisdictionOptionLabel(j),
      })),
    [jurisdictionLicenses],
  );

  // Filter jurisdictions based on selection and only include active licenses (exclude deleted and cancelled)
  const filteredJurisdictions = React.useMemo(() => {
    const jurisdictions =
      selectedJurisdictions.length === 0
        ? jurisdictionLicenses
        : jurisdictionLicenses.filter((jurisdiction) =>
            selectedJurisdictions.includes(jurisdiction.display_code),
          );

    // Only include active licenses (not deleted, not cancelled) and filter by license type if selected
    return jurisdictions
      .map((jurisdiction) => ({
        ...jurisdiction,
        licenses: jurisdiction.licenses.filter((l) => {
          if (
            l.is_deleted ||
            l.status === "CANCELLED" ||
            l.status === "CANCELLED_REQUIRES_PAYMENT"
          ) {
            return false;
          }

          // If no license types are selected, include all
          if (selectedLicenseTypes.length === 0) return true;

          // Only include licenses that match selected license types
          return selectedLicenseTypes.includes(l.license_type.id);
        }),
      }))
      .filter((jurisdiction) => jurisdiction.licenses.length > 0);
  }, [selectedJurisdictions, selectedLicenseTypes, jurisdictionLicenses]);

  /** All Active tab: non-deleted, non-cancelled registrations (includes bulk-pay cohort). */
  const allActiveRegistrationsJurisdictions = React.useMemo(
    () =>
      filteredJurisdictions.map((j) => ({
        ...j,
        licenses: [...j.licenses],
      })),
    [filteredJurisdictions],
  );

  /** Pay Later registrations eligible for bulk payment (matches invoice-style fee cohort). */
  const bulkPaymentPayLaterJurisdictions = React.useMemo(() => {
    const out: JurisdictionLicenses[] = [];
    for (const j of filteredJurisdictions) {
      const licenses = j.licenses.filter((l) =>
        Boolean(l.bulk_payment_pay_later_eligible),
      );
      if (licenses.length > 0) {
        out.push({ ...j, licenses });
      }
    }
    return out;
  }, [filteredJurisdictions]);

  const complianceIssueLicenses = React.useMemo(() => {
    return filteredJurisdictions
      .map((jurisdiction) => ({
        ...jurisdiction,
        licenses: jurisdiction.licenses.filter(
          (license) => attentionColumnParts(license).compliance,
        ),
      }))
      .filter((jurisdiction) => jurisdiction.licenses.length > 0);
  }, [filteredJurisdictions]);

  const needsAttentionLicenses = React.useMemo(() => {
    return filteredJurisdictions
      .map((jurisdiction) => ({
        ...jurisdiction,
        licenses: jurisdiction.licenses.filter(
          (license) =>
            Boolean(license.needs_attention) &&
            !attentionColumnParts(license).compliance,
        ),
      }))
      .filter((jurisdiction) => jurisdiction.licenses.length > 0);
  }, [filteredJurisdictions]);

  /** Registrations whose display status is Balance Due. */
  const balanceDueLicenses = React.useMemo(() => {
    return filteredJurisdictions
      .map((jurisdiction) => ({
        ...jurisdiction,
        licenses: jurisdiction.licenses.filter((l) =>
          matchesRegistrantBalanceDueTileCohort(l),
        ),
      }))
      .filter((jurisdiction) => jurisdiction.licenses.length > 0);
  }, [filteredJurisdictions]);

  // Get renewable licenses for the renewable tab (Renewal Due / Renewal Overdue)
  const renewableLicenses = React.useMemo(() => {
    return filteredJurisdictions
      .map((jurisdiction) => ({
        ...jurisdiction,
        licenses: jurisdiction.licenses.filter((l) =>
          matchesRegistrantRenewableTileCohort(l),
        ),
      }))
      .filter((jurisdiction) => jurisdiction.licenses.length > 0);
  }, [filteredJurisdictions]);

  // Get inactive licenses for the inactive tab (deleted or cancelled)
  const inactiveLicenses = React.useMemo(() => {
    const jurisdictions =
      selectedJurisdictions.length === 0
        ? jurisdictionLicenses
        : jurisdictionLicenses.filter((jurisdiction) =>
            selectedJurisdictions.includes(jurisdiction.display_code),
          );

    // Include inactive licenses (deleted or cancelled) and filter by license type if selected
    return jurisdictions
      .map((jurisdiction) => ({
        ...jurisdiction,
        licenses: jurisdiction.licenses.filter((l) => {
          if (
            !l.is_deleted &&
            l.status !== "CANCELLED" &&
            l.status !== "CANCELLED_REQUIRES_PAYMENT"
          ) {
            return false;
          }

          // If no license types are selected, include all
          if (selectedLicenseTypes.length === 0) return true;

          // Only include licenses that match selected license types
          return selectedLicenseTypes.includes(l.license_type.id);
        }),
      }))
      .filter((jurisdiction) => jurisdiction.licenses.length > 0);
  }, [selectedJurisdictions, selectedLicenseTypes, jurisdictionLicenses]);

  return {
    jurisdictionLicenses,
    jurisdictionOptions,
    filteredJurisdictions,
    serverRowOrder,
    allActiveRegistrationsJurisdictions,
    bulkPaymentPayLaterJurisdictions,
    needsAttentionLicenses,
    complianceIssueLicenses,
    balanceDueLicenses,
    renewableLicenses,
    inactiveLicenses,
    has_multiple_registrant_accounts,
    registrantOptions,
  };
}
